import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Config } from '../_store';
import { AuthenticationService } from '../_services/authenticationService';
import { UtilityService } from '../_services/utility.service';
import { DataService } from '../_services/data.service';
import { CommonService } from '../_services/common.service';
import { FilterService } from '../_services/filter.service';
import { StorageService } from '../_services/storage.service';
import { StringConstants } from '../_constants/stringConstants';
import { NavController } from '@ionic/angular';
import { RouteNavigationService } from '../_services/routeNavigationService';
import { AppConfigService } from '../_services/appConfigService';
import { CommonDataService } from '../appDataServices/commonDataService';
import { DateUtilsService } from '../_services/dateUtils';
import { AppStateService } from '../_services/state.service';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyDisplayService } from '../_services/currency-display.service';
import { CfMockService } from '../_services/cf-mock.service';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {

  private invNumber: string = 'LABELS.INVOICE_NUMBER';
  private invNumberSearch: string = 'LABELS.INVOICE_NUMBER_SEARCH';
  private multipayTag: string = 'LABELS.MULTIPAY_TAG';
  private buyers: string = 'LABELS.BUYER(S)';
  private invDate: string = 'LABELS.INVOICE_DATE';
  private cashDiscount: string = 'LABELS.CASH_DISCOUNT';
  private discount: string = 'LABELS.discount';
  private dueToday: string = 'LABELS.DUE_TODAY';
  private overdues: string = 'LABELS.OVERDUES';
  private type: string = 'LABELS.TYPE';
  private currency: string = 'LABELS.CURRENCY';
  private seller: string = 'LABELS.SELLERS';
  private overdueBy: string = this.translate.instant('LABELS.OVERDUE_BY');
  private days: string = this.translate.instant('LABELS.DAY');



  invoiceData: any = {
    maxDate: 0
  };

  private preserveInvoiceFilters = false;

  currentInvoice: any = {};

  private invoicesUpdateBehavior: BehaviorSubject<any> = new BehaviorSubject(null);
  public invoicesUpdate = this.invoicesUpdateBehavior.asObservable();

  private sellerUIConfig: any = {};


  constructor(
    private authenticationService: AuthenticationService,
    private utilityService: UtilityService,
    private dataService: DataService,
    private filterService: FilterService,
    private commonService: CommonService,
    private appConfigService: AppConfigService,
    private commonDataService: CommonDataService,
    private dateUtils: DateUtilsService,
    private appStateService: AppStateService,
    private translate: TranslateService,
    private currencyDisplayService: CurrencyDisplayService,
    private cfMockService: CfMockService
  ) {
    this.authenticationService.currentUser.subscribe(x => {
      if (!x) {
        this.invoiceData = {
          maxDate: 0
        };
        this.setPreserveInvoiceFiltersFlag(false);
      }
    });
    // this.commonDataService.getUISellerConfig().subscribe(data => {
    //   this.sellerUIConfig = data;
    // });
  }

  private getReasonsForInvoiceDispute(reason) {
    return {
      action: Config.webActions.DISPUTE_INVOICE_REASONS,
      DC: reason.invoice.DC,
      payex_cpg_code: reason.invoice.CC
    };
  }

  private getNeftBankListRequest() {
    return {
      "action": "neft_banks"
    };
  }

  private getRegisterIBPRequest(ibpData) {
    const userProfile = this.authenticationService.currentUserValue;
    return {
      "action": "neft_reg",
      "corpid": ibpData.corpid,
      "userid": ibpData.userid,
      "username": ibpData.aliasid,
      "payex_retailer_code": userProfile.userCode,
      "acc_no": ibpData.acc_no,
      "acc_nor": ibpData.acc_nor
    };
  }

  private getNeftStatementRequest() {
    const userProfile = this.authenticationService.currentUserValue;
    return {
      RC: userProfile.userCode,
      action: "neft_stm"
    };
  }

  getInvoiceData(options: any = {}) {
    const prevMaxDate = this.invoiceData.maxDate;
    return this.commonDataService.getList(
      this.commonDataService.getRequestWithLastAccessTime(options.hardRefresh ? 0 : this.invoiceData.maxDate), 'GETINVOICES',
      (data) => {
        this.invoiceData = this.enhanceInvoiceResponse(data);
        this.invoiceData.maxDate = data.serverTime;
        this.invoiceData.prevMaxDate = prevMaxDate;
        this.getInvoiceDetails().subscribe(data => { })
        const cashDiscountList = this.getCashDiscountDataForInvoiceList();
        //map
        console.log('cashDiscountList', data);


        return this.invoiceData;
      });
  }

  getMultipayInvoicesWithDeductions() {
    const userProfile = this.authenticationService.currentUserValue;
    const req = {
      ret_code: userProfile.userCode,
      IMEI: this.commonService.imei(),
      action: Config.webActions.GET_MULTIPAY_WITH_DEDUCTIONS
    };
    const multipayGroups = {};
    return this.utilityService.httpPost(req, this.appConfigService.urls.invoiceUrl).pipe(map(data => {
      const invoices = data.data.invoices.filter(item => item.BA > 0);
      const todayDate = moment().startOf('day');

      const userDistributorData1 = {};
      userDistributorData1['currCode'] = Config.currencySymbolConfig.codeSymbol;
      (userProfile.distributors || []).forEach((item) => {
        if (item.currCode) {
          userDistributorData1['currCode'] = item.currCode;
        }
      });
      invoices.forEach(item => {
        if (!item.OC) {
          item.OC = 'NA';
        }
        if (!multipayGroups[item.OC]) {
          multipayGroups[item.OC] = [];
        }
        item.currCode = `${userDistributorData1['currCode']}`;
        this.enhanceSingleInvoice(item, todayDate, '');
        multipayGroups[item.OC].push(item);
      });
      return multipayGroups;
    }));
  }

  private enhanceInvoiceResponse(invoiceData): any {
    const localInvoiceData = this.invoiceData;
    const currentInvoiceList = localInvoiceData.invoices || [];
    const todayDate = this.dateUtils.getToday();
    const invoiceMap = {};
    const sellerMap = {};
    const docTypeMap = {};
    const currencyCodeMap = {};
    const multipayTagsMap = {};
    const buyerMap = {};
    const sellerMessages = {};
    const invsWithRibbon = {};
    const invMap = {};
    let overDueDocsCount = 0
    if (currentInvoiceList) {
      currentInvoiceList.forEach(invoice => {
        invoiceMap[invoice.docId] = invoice;
      });
    }
    if (!invoiceData || !invoiceData.invoices) {
      invoiceData = invoiceData || {};
      invoiceData.invoices = {};
    }
    const receivedInvoices = Object.keys(invoiceData.invoices);
    const isSalesmanLogin = this.authenticationService.isCurrentUserSalesman();
    receivedInvoices.forEach(invDocId => {
      const invoice = invoiceData.invoices[invDocId];
      const uiConfigForSeller = this.sellerUIConfig[invoice.sellerCode] || {};
      const sellerConfig = Object.assign({}, invoiceData.sellerConfig[invoice.sellerCode], uiConfigForSeller);
      console.log(sellerConfig);
      
      this.enhanceSingleInvoice(invoice, sellerConfig, todayDate, isSalesmanLogin);
      if (invoiceMap[invoice.docId]) {
        Object.assign(invoiceMap[invoice.docId], invoice);
      }
      if (invoiceMap[invoice.docId]) {
        if (invoice.status !== 'UNPAID') {
          const invIndex = currentInvoiceList.findIndex(listItem => listItem.docId === invoice.docId);
          currentInvoiceList.splice(invIndex, 1);
        }
      } else if (invoice.status === 'UNPAID') {
        currentInvoiceList.push(invoice);
        invoiceMap[invoice.docId] = invoice;
      }
      invoice.orgMultipayTag = invoice.multipayTag;
    });
    currentInvoiceList.forEach(invoice => {
      invoice.selected = false;
      invoice.selectionDisabled = false;
      invoice.isVisible = true;
      // UI Hack to stop user from paying docs with different currencies and with different buyer codes
      invoice.multipayTag = `${invoice.orgMultipayTag}_${invoice.currCode || 'USD'}_${invoice.buyerCode}`;
      if (!sellerMap[invoice.sellerCode]) {
        sellerMap[invoice.sellerCode] = invoice.sellerName;
      }
      if (!docTypeMap[invoice.docType]) {
        docTypeMap[invoice.docType] = invoice.docType;
      }
      if (invoice.currCode) {
        if (!currencyCodeMap[(invoice.currCode).trim()]) {
          currencyCodeMap[(invoice.currCode).trim()] = (invoice.currCode).trim();
        }
      }
      if (!multipayTagsMap[invoice.multipayTag]) {
        multipayTagsMap[invoice.multipayTag] = invoice.multipayTag;
      }
      if (!buyerMap[invoice.buyerNameWithCode]) {
        buyerMap[invoice.buyerNameWithCode] = invoice.buyerNameWithCode;
      }
      if (invoice.sellerMsg) {
        if (!sellerMessages[invoice.sellerMsg]) {
          sellerMessages[invoice.sellerMsg] = invoice.sellerMsg;
        }
      }
      if (invoice.hasRibbonData || true) {
        if (!invsWithRibbon[invoice.sellerCode]) {
          invsWithRibbon[invoice.sellerCode] = [];
        }
        invsWithRibbon[invoice.sellerCode].push(invoice.docId)
      }
      invoice.messages = [];
      overDueDocsCount += invoice.overDueDays > 0 ? 1 : 0
      invMap[`${invoice.sellerCode}_${invoice.docId}`] = invoice;
    });
    // Remove invoices marked as paid via CF mock service
    for (let i = currentInvoiceList.length - 1; i >= 0; i--) {
      const inv = currentInvoiceList[i];
      if (this.cfMockService.checkIfInvoicesPaidViaCFMock(inv.docId)) {
        currentInvoiceList.splice(i, 1);
      }
    }
    // Remove LRP invoices so that those can be added back at start of list after sort
    for (let i = currentInvoiceList.length - 1; i >= 0; i--) {
      const inv = currentInvoiceList[i];
      if (inv.docType === 'LRP') {
        currentInvoiceList.splice(i, 1);
      }
    }
    currentInvoiceList.sort((a, b) => (a.docNo > b.docNo) ? 1 : -1);
    currentInvoiceList.sort((a, b) => a.dueDtForSort - b.dueDtForSort);
    // Add CF Generated invoices
    const cfInvoices = this.cfMockService.getCurrentCFGeneratedInvoices() || [];
    cfInvoices.forEach(inv => {
      const uiConfigForSeller = this.sellerUIConfig[inv.sellerCode] || {};
      const sellerConfig = Object.assign({}, invoiceData.sellerConfig[inv.sellerCode], uiConfigForSeller);
      this.enhanceSingleInvoice(inv, sellerConfig, todayDate, isSalesmanLogin);
      currentInvoiceList.unshift(inv);
    });
    const retData: any = {
      invoices: currentInvoiceList,
      sellers: Object.keys(sellerMap).map((sellerCd) => sellerMap[sellerCd]),
      docTypes: Object.keys(docTypeMap).map((docType) => docTypeMap[docType]),
      buyers: Object.keys(buyerMap).map((buyer) => buyerMap[buyer]),
      multipayTags: Object.keys(multipayTagsMap).map((mPayTag) => multipayTagsMap[mPayTag]),
      invCount: currentInvoiceList.length,
      sellerMessages: Object.keys(sellerMessages).map((sellerMsg) => sellerMessages[sellerMsg]),
      invsWithRibbon: Object.keys(invsWithRibbon).map((key) => {
        return { sellerCode: key, docIds: invsWithRibbon[key] };
      }),
      invMap,
      currencies: Object.keys(currencyCodeMap).map((currCode) => currencyCodeMap[currCode]),
      overDueDocsCount: overDueDocsCount
    };
    retData.filters = this.getInvoiceFilters(retData, this.preserveInvoiceFilters ? localInvoiceData : {});
    return retData;
  }

  public enhanceSingleInvoice(item, sellerConfig: any = {}, todayDate, isSalesmanLogin = false) {
    item.allowPay = sellerConfig.allowPay;
    if (item.disablePay) {
      item.allowPay = false;
    }
    item.sellerName = item.sellerName || sellerConfig.sellerNm || '';
    item.showDocBadge = sellerConfig.showInvType;
    item.imgUrl = item.imgUrl || sellerConfig.sellerImg;
    console.log(item.imgUrl);
    item.allowReject = sellerConfig.allowReject;
    item.allowPay = sellerConfig.allowPay;
    item.multiPayAllowed = (typeof sellerConfig.multiPayAllowed !== 'undefined') ? sellerConfig.multiPayAllowed : true;
    item.invoiceInfo = {};
    item.formattedDocAmount = this.currencyDisplayService.getFormattedCurrencyStr({ amt: item.docAmt, curr: item.currCode || Config.defaultCurr });
    item.formattedAmount = this.currencyDisplayService.getFormattedCurrencyStr({amt: item.dueAmt, curr: item.currCode});
    // Dates
    const invDueDateMoment = this.dateUtils.convertDateUnknownFormat(item.dueDate);
    item.invDueDate = this.dateUtils.getFormattedDateForDisplay(invDueDateMoment);
    const invoiceDateMoment = this.dateUtils.convertDateUnknownFormat(item.docDate);
    item.invoiceDate = this.dateUtils.getFormattedDateForDisplay(invoiceDateMoment);
    //
    item.overDueDays = this.dateUtils.getDateDiff(todayDate, invDueDateMoment);
    if (item.overDueDays > 0) {
      item.dueMessage = `${this.translate.instant('LABELS.OVERDUE_MESSAGE', { days: item.overDueDays })}`;
      item.colorClass = 'colorError';
      item.overduePenalty = this.getOverduePenalty(item.overDueDays, item.dueAmt, item.currCode);
    } /*else if (item.overDueDays === 0) {
      item.dueMessage = this.dueToday;
      item.colorClass = 'colorWarn';
    }*/
    item.dueDtForSort = this.dateUtils.getTimestamp(invDueDateMoment);
    item.docDtForSort = this.dateUtils.getTimestamp(invoiceDateMoment);
    if (typeof item.allowPay === 'undefined') {
      item.allowPay = true;
    }
    if (typeof item.allowReject === 'undefined') {
      item.allowReject = true;
    }
    // if (item.sellerName === 'Emcure') {
    //   item.docViewPath = 'http://13.235.178.169/invoices/emcure/SampleSaleInvoice.pdf';
    // }
    if (!item.buyerName) {
      item.buyerName = item.buyerCode;
    }
    item.sellerMsg = sellerConfig.sellerMsg || '';
    if (isSalesmanLogin) {
      item.allowPay = false;
    }
    item.showRTP = sellerConfig.showRTP && !item.requestToPay && isSalesmanLogin;
    if (item.requestToPay) {
      if (!item.statusMessages) {
        item.statusMessages = [];
      }
      if (!item.statusMessages.find(msg => msg.type === 'rtpMsg')) {
        item.statusMessages.push({ type: 'overDueInv1', msg: 'RTP Link Generated', label: 'LABELS.RTP_LINK_GENERATED' })
      }
    }
    console.log("item.allowPay : ", item.allowPay);

  }

  // Overdue penalty is bucketed by how many days the invoice is overdue.
  // (Dummy slabs for the offline build — tune percentages as needed.)
  private overduePenaltySlabs = [
    { maxDays: 10, percentage: 0.5 },
    { maxDays: 30, percentage: 1 },
    { maxDays: 60, percentage: 1.5 },
    { maxDays: Infinity, percentage: 2 }
  ];

  private getOverduePenalty(overDueDays, dueAmt, currCode) {
    const slab = this.overduePenaltySlabs.find(s => overDueDays <= s.maxDays);
    const percentage = slab ? slab.percentage : 0;
    const penaltyAmt = (Number(dueAmt) || 0) * percentage / 100;
    return {
      percentage: `${percentage}%`,
      amount: this.currencyDisplayService.getFormattedCurrencyStr({ amt: penaltyAmt, curr: currCode })
    };
  }

  submitRejectInvoice(rejectOption, payData) {
    const req = {};
    return this.utilityService.httpPost(req, this.appConfigService.urls.invoiceUrl).pipe(map(data => {
      return this.enhanceRejectInvoiceResponse(data);
    }));
  }

  enhanceRejectInvoiceResponse(response) {
    return response;
  }

  getDisputedInvoiceReasons(reasons) {
    const req = this.getReasonsForInvoiceDispute(reasons);
    return this.utilityService.httpPost(req, this.appConfigService.urls.invoiceUrl).pipe(map(data => {
      return this.enhanceDisputedInvoiceResponse(data);
    }));
  }

  enhanceDisputedInvoiceResponse(response) {

    response.data.forEach((reason, nReason) => {
      reason.index = nReason + 1;
    });

    return response;
  }

  submitRegisterIBPForm(registerIBPData) {
    const req = this.getRegisterIBPRequest(registerIBPData);
    return this.utilityService.httpPost(req, this.appConfigService.urls.invoiceUrl).pipe(map(data => {
      return data;
    }));
  }

  getNeftBankList() {
    const req = this.getNeftBankListRequest();
    return this.utilityService.httpPost(req, this.appConfigService.urls.invoiceUrl).pipe(map(data => {
      if (data.data) {
        data.data.sort((a, b) => {
          return a.bankName > b.bankName ? 1 : -1
        });
      }
      return data.data;
    }));
  }

  getNeftStatement() {
    const req = this.getNeftStatementRequest();
    return this.utilityService.httpPost(req, this.appConfigService.urls.invoiceUrl).pipe(map(data => {
      return data.data;
    }));
  }

  private getInvoiceFilters(invoiceData, localInvoiceData) {
    const amountProp = 'dueAmtDetails';
    const sellerFiltersObj = {
      company: {
        rules: invoiceData.sellers.map((sellerName) => {
          return {
            label: sellerName,
            fn: item => item.sellerName === sellerName
          };
        }).sort((a, b) => {
          if (a.label < b.label)
            return -1;
          if (a.label > b.label)
            return 1;
          return 0;
        }),
        label: this.seller,
        amountProp: amountProp
      },
      buyers: {
        rules: invoiceData.buyers.map((buyerNameWithCode) => {
          return {
            label: buyerNameWithCode,
            fn: item => item.buyerNameWithCode === buyerNameWithCode
          };
        }).sort((a, b) => {
          if (a.label < b.label)
            return -1;
          if (a.label > b.label)
            return 1;
          return 0;
        }),
        label: `${this.buyers}`,
        amountProp: amountProp
      },
      type: {
        rules: invoiceData.docTypes.map((docType) => {
          return {
            label: docType,
            fn: item => item.docType === docType
          };
        }),
        label: this.type,
        amountProp: amountProp
      },
      currency: {
        rules: invoiceData.currencies.map((currCode) => {
          return {
            label: currCode,
            fn: item => (item.currCode || '').trim() === currCode
          };
        }),
        label: this.currency,
        amountProp: amountProp
      },
      overdue: {
        rules: [{
          label: this.overdues,
          fn: item => (item.overDueDays > 0)
        }],
        label: this.overdues,
        amountProp: amountProp
      },
      dueToday: {
        rules: [{
          label: this.dueToday,
          fn: item => (item.overDueDays === 0)
        }],
        label: this.dueToday,
        amountProp: amountProp
      },
      discount: {
        rules: [{
          label: this.cashDiscount,
          fn: item => (item.isCashdiscount)
        }],
        label: this.discount,
        amountProp: amountProp
      },
      invoiceCreateDate: {
        isTemplate: true,
        isCreateDate: true,
        rules: [{
          label: this.invDate,
          fn: (item, rule) => {
            if (rule.fromCreateDate, rule.toCreateDate) {
              return this.dateUtils.dateIsBetween(item.invoiceDate, rule.fromCreateDate, rule.toCreateDate);
            } else {
              return true;
            }
          }
        }],
        label: this.invDate,
        amountProp: amountProp
      },
      invoiceNumSearch: {
        isValueFilter: true,
        rules: [{
          label: this.invNumber,
          fn: (item, val) => {
            return item.docNo.toString().toLowerCase().indexOf(val.toLowerCase()) !== -1;
          }
        }],
        label: this.invNumberSearch,
        amountProp: amountProp
      },
      miltiPayTag: {
        rules: invoiceData.multipayTags.map((mTag) => {
          return {
            label: mTag,
            fn: item => item.multipayTag === mTag
          };
        }),
        label: this.multipayTag,
        amountProp: amountProp,
        hiddenFilter: true
      }
    };
    const isSalesmanLogin = this.authenticationService.isCurrentUserSalesman();
    const filtersObj = Object.assign({}, sellerFiltersObj);
    return this.filterService.createFilters(invoiceData.invoices, filtersObj, 'docId', localInvoiceData.filters);
  }

  getCurrentInvoicesData() {
    return this.invoiceData;
  }

  disableInvoicesForMultiPayments(invoiceList, multipayTag) {
    invoiceList.forEach(inv => {
      let isDifferentMultipayTag = false;
      if ((inv.multipayTag || '') !== multipayTag) {
        isDifferentMultipayTag = true
      }
      inv.selectionDisabled = inv.selectionDisabled || isDifferentMultipayTag;
    });
  }

  selectMultipleInvoices(invoices) {
    invoices.forEach(inv => {
      inv.selected = true;
    });
  }

  getSelectedInvoicesData() {
    const selectedInvoices = (this.invoiceData.invoices && this.invoiceData.invoices.filter(inv => inv.selected)) || [];
    this.invoiceData.selectedInvoicesCount = selectedInvoices.length;
    let totalAmount = 0;
    if (selectedInvoices.length) {
      totalAmount = selectedInvoices.reduce((total, inv) => {
        return total + (inv.dueAmt || 0);
      }, 0);
      totalAmount = this.dataService.roundNumToTwoDecimalPlace(totalAmount);
    }
    if (selectedInvoices.length) {
      this.invoiceData.totalAmountFormatted = this.currencyDisplayService.getFormattedCurrencyStr({ amt: totalAmount, curr: selectedInvoices[0].currCode });
    }
    return {
      selectedInvoices: selectedInvoices,
      totalAmount: totalAmount,
      totalAmountFormatted: this.invoiceData.totalAmountFormatted
    };
  }

  getSelectedAndVisibleInvoices() {
    return this.invoiceData.invoices.filter(inv => inv.selected && inv.isVisible);
  }

  getSelectedInvoices() {
    return this.invoiceData.invoices.filter(inv => inv.selected);
  }

  clearInvoiceSelectionFlag(invoiceList, clearSelected = false) {
    invoiceList.forEach(inv => {
      inv.selectionDisabled = false;
      if (clearSelected) {
        inv.selected = false;
      }
    });
  }

  setPreserveInvoiceFiltersFlag(flagToSet = true) {
    this.preserveInvoiceFilters = flagToSet;
  }

  getDivisionWiseOutstandingInfo(cpgData, clickedSection, division = undefined) {
    const userProfile = this.authenticationService.currentUserValue;
    const req = {
      action: Config.webActions.DIVISION_OUTSTANDING,
      RC: userProfile.userCode,
      DC: cpgData.DC,
      CC: cpgData.CC,
      distributorName: cpgData.DN,
      CR: cpgData.CR,
      amount: cpgData.amt,
      clickedSection: clickedSection,
      FK: division
    }
    return this.utilityService.httpPost(req, this.appConfigService.urls.invoiceUrl).pipe(map(data => {
      const invoiceData = this.invoiceData;
      const cpgDivData = this.invoiceData.otherData.uniqueCPGsData[cpgData.TAG];
      if (!data.data || !data.data.length) {
        if (clickedSection === -1) {
          data.data = [{
            TYPE: 'ALL'
          }];
        } else {
          data.data = [];
        }
      }
      const divisionWiseData = data.data.map((item, nItem) => {
        if (clickedSection === -1) {
          item.clickSection = nItem;
          const divData = cpgDivData.DIVISIONS[item.TYPE] || {
            INV: 0,
            DEB: 0,
            INVAMT: 0,
            DEBAMT: 0
          };
          if (item.TYPE === 'ALL') {
            item.SUM = cpgData.amt;
            item.COUNT = (divData.INV + divData.DEB)
          }
          item.details = [{
            type: 'Invoices',
            count: divData.INV,
            amt: divData.INVAMT
          }, {
            type: 'Debit Notes',
            count: divData.DEB,
            amt: divData.DEBAMT
          }]
        } else {
          return {
            type: item.TYPE || 'NA',
            count: item.COUNT,
            amt: item.SUM
          };
        }
        return item;
      });
      return divisionWiseData;
    }));
  }

  confirmInvoiceForCD(invoices) {
    const userProfile = this.authenticationService.currentUserValue;
    const req = {
      NOI: invoices.length,
      IMEI: this.commonService.imei(),
      RC: userProfile.userCode,
      TI: invoices.map(inv => inv.TI),
      action: Config.webActions.CONFIRM_INVOICE_FOR_CD,
      OM: 'retailer',
      DC: invoices[0].DC,
    };
    return this.utilityService.httpPost(req, this.appConfigService.urls.invoiceUrl).pipe(map(data => {
      return data;
    }));
  }

  getInvoiceDetails() {
    return this.utilityService.httpPost(
      Object.assign({ action: Config.webActions.GETRIBBON }, this.commonDataService.getRequestWithLastAccessTime(this.invoiceData.prevMaxDate), { docDetails: this.invoiceData.invsWithRibbon }),
      this.commonService.getInterfaceUrl('GETRIBBON')
    ).pipe(map(data => {
      const ribbonData = data.data || {};
      Object.keys(ribbonData).forEach(sellerCode => {
        const sellerInvoices = ribbonData[sellerCode];
        sellerInvoices.forEach(inv => {
          const existingInv = this.invoiceData.invMap[`${sellerCode}_${inv.docId}`];
          if (existingInv) {
            if (!existingInv.messages) {
              existingInv.messages = [];
            }
            existingInv.messages = existingInv.messages.concat(inv.msgs);
          }
        });
      });
      return data.data;
    }));
  }

  getCashDiscountDataForInvoiceList() {
    (this.invoiceData.invsWithRibbon || []).forEach(sellerWithInvs => {
      const req = {
        sellerCode: sellerWithInvs.sellerCode,
        payDate: this.dateUtils.getFormattedDate(),
        docId: sellerWithInvs.docIds,
      };
      return this.utilityService.httpPost(req, this.commonService.getInterfaceUrl('GET_CASHDISCOUNT_FOR_INVOICES_LIST')
      ).pipe(map(data => {
        const cdInvoices = (data.data || {}).invoice || [];
        cdInvoices.forEach(item => {
          const existingInv = this.invoiceData.invMap[`${sellerWithInvs.sellerCode}_${item.docId}`];
          if (existingInv && item.text) {
            if (!existingInv.messages) {
              existingInv.messages = [];
            }
            existingInv.messages.push({
              msg: item.text,
              type: 'nonoverDueInv'
            });
          }
        });
      })).subscribe(data => {
      });
    });
  }

  createRequestToPay(invoices) {
    const req = {
      sellerCode: invoices[0].sellerCode,
      buyerCode: invoices[0].buyerCode,
      docIds: invoices.map(inv => inv.docId),
      payAmt: invoices.reduce((totalAmt, inv) => totalAmt + inv.dueAmt, 0),
      tpin: ''
    };
    return this.utilityService.httpPost(req, this.commonService.getInterfaceUrl('CREATE_RTP_LINK'));
  }

}

